import { createContext, useContext, useRef, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { buildServiceUrl } from "./urlBuilder";
import { runtimeEnv } from "./runtimeEnv";
import { getAdminConnectorUpdateEventName } from "./auth/adminConnectorRuntime";
import { redirectToUnauthorized } from "./auth/redirectToUnauthorized";
import { useLogout } from "../hooks/useLogout";

const WorkerContext = createContext();

export const useWorker = () => {
  const context = useContext(WorkerContext);
  if (!context) {
    throw new Error("useWorker must be used within a WorkerProvider");
  }
  return context;
};

export const WorkerProvider = ({ children }) => {
  const workerRef = useRef(null);
  const { user } = useAuth();
  const logout = useLogout();
  const logoutRef = useRef(logout);
  const [runtimePortVersion, setRuntimePortVersion] = useState(0);
  const token = user?.access_token || user?.id_token || "";
  const apiUrl = buildServiceUrl("springConnector", "", { token });

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    const worker = new Worker("/cache-worker.js");
    workerRef.current = worker;

    const handleMessage = (event) => {
      if (event.data.type === "AUTH_ERROR") {
        if (event.data.status === 401) {
          logoutRef.current({ redirectPath: "/session-expired" });
        } else if (event.data.status === 403) {
          redirectToUnauthorized();
        }
      }
    };

    worker.addEventListener("message", handleMessage);

    const handleOnline = () => worker.postMessage({ type: "SYNC" });
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
      worker.removeEventListener("message", handleMessage);
      worker.terminate();
      if (workerRef.current === worker) {
        workerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const updateEventName = getAdminConnectorUpdateEventName();
    const handleRuntimeUpdate = () =>
      setRuntimePortVersion((currentVersion) => currentVersion + 1);

    window.addEventListener(updateEventName, handleRuntimeUpdate);

    return () => {
      window.removeEventListener(updateEventName, handleRuntimeUpdate);
    };
  }, []);

  useEffect(() => {
    if (!workerRef.current) {
      return;
    }

    workerRef.current.postMessage({
      type: "SET_CONFIG",
      config: {
        generic_Endpoint: apiUrl,
        genericEndpoint: apiUrl,
        hmacSecret: runtimeEnv.REACT_APP_HMAC_SECRET,
      },
    });

    workerRef.current.postMessage({ type: "SET_TOKEN", jwt: token });
  }, [apiUrl, runtimePortVersion, token]);

  return (
    <WorkerContext.Provider value={workerRef.current}>
      {children}
    </WorkerContext.Provider>
  );
};
